import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Pencil, Upload } from 'lucide-react'
import {
  useBulkStatusUpdate,
  useCollectionTargetMutations,
  useCollectionTargets,
  useTargetSummary,
} from '../../../hooks'
import { downloadSchedule } from '../../../services/collection-targets'
import { BulkStatusUpdateBar } from '../../../components/collections/BulkStatusUpdateBar'
import { CollectionProgressBar } from '../../../components/collections/CollectionProgressBar'
import { CollectionTargetsTable } from '../../../components/collections/CollectionTargetsTable'
import { RequestHealthIndicator } from '../../../components/collections/RequestHealthIndicator'
import type { PackageStatus, ReturnStatus } from '../../../types/collection-requests'
import { StatusOverrideControl } from './StatusOverrideControl'
import { StallControl } from './StallControl'

interface ExpandedRequestDetailProps {
  caseId: string
  requestId: string
  packageStatus: PackageStatus
  returnStatus: ReturnStatus
  stallReason: string | null
  isLocked: boolean
  onShowPackageBuild: () => void
  onShowScheduleImport: () => void
  onShowUpload: () => void
  onEdit: () => void
  onRefetch: () => void
}

interface ActionButtonsProps {
  packageStatus: PackageStatus
  onEdit: () => void
  onShowPackageBuild: () => void
  onExportSchedule: () => void
  onShowScheduleImport: () => void
  onShowUpload: () => void
}

function ActionButtons(props: ActionButtonsProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const {
    packageStatus,
    onEdit,
    onShowPackageBuild,
    onExportSchedule,
    onShowScheduleImport,
    onShowUpload,
  } = props
  return (
    <div className="items-centre flex gap-2">
      {packageStatus === 'pending' && (
        <button
          onClick={onEdit}
          className="items-centre inline-flex gap-1.5 rounded-lg border border-[#4e5e82]/15 px-3 py-1.5 text-xs font-medium text-[#5c6e94] hover:bg-lime-100 dark:border-[#5c6e94] dark:text-lime-400 dark:hover:bg-[#8a9bba]/15"
        >
          <Pencil className="h-3.5 w-3.5" />
          {t('collections.editRequest')}
        </button>
      )}
      <button
        onClick={onShowPackageBuild}
        className="items-centre inline-flex gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <Download className="h-3.5 w-3.5" />
        {t('collections.packageBuild.title')}
      </button>
      <button
        onClick={onExportSchedule}
        className="items-centre inline-flex gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <Download className="h-3.5 w-3.5" />
        {t('collections.scheduleExport.title')}
      </button>
      <button
        onClick={onShowScheduleImport}
        className="items-centre inline-flex gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <Upload className="h-3.5 w-3.5" />
        {t('collections.scheduleImport.title')}
      </button>
      <button
        onClick={onShowUpload}
        className="items-centre inline-flex gap-1.5 rounded-lg bg-[#4e5e82] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#5c6e94]"
      >
        <Upload className="h-3.5 w-3.5" />
        {t('detail.collections.uploadReceipt.title')}
      </button>
    </div>
  )
}

type TargetSummary = ReturnType<typeof useTargetSummary>['summary']

function SummaryHeader({ summary }: { summary: TargetSummary }): React.ReactElement | null {
  if (!summary) return null
  return (
    <div className="items-centre flex gap-4">
      <div className="flex-1">
        <CollectionProgressBar
          statusCounts={summary.statusCounts}
          totalTargets={summary.totalTargets}
          progressPercentage={summary.progressPercentage}
        />
      </div>
      <RequestHealthIndicator healthStatus={summary.healthStatus} />
    </div>
  )
}

export function ExpandedRequestDetail(props: ExpandedRequestDetailProps): React.ReactElement {
  const { caseId, requestId, packageStatus, returnStatus, stallReason, isLocked } = props
  const { onShowPackageBuild, onShowScheduleImport, onShowUpload, onEdit, onRefetch } = props
  const {
    targets,
    loading: targetsLoading,
    refetch: refetchTargets,
  } = useCollectionTargets(caseId, requestId)
  const { summary } = useTargetSummary(caseId, requestId)
  const { update: bulkUpdate, updating } = useBulkStatusUpdate()
  const { handleRemoveTargets, removingTargets } = useCollectionTargetMutations(caseId, requestId)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const handleBulkUpdate = (newStatus: string, note?: string): void => {
    void (async () => {
      const targetIds = Array.from(selectedIds)
      await bulkUpdate(caseId, requestId, note === undefined ? { targetIds, newStatus } : { targetIds, newStatus, note })
      setSelectedIds(new Set())
      onRefetch()
    })()
  }

  const handleRemoveSelected = (): void => {
    void (async () => {
      const result = await handleRemoveTargets(Array.from(selectedIds))
      if (result) {
        setSelectedIds(new Set())
        void refetchTargets()
        onRefetch()
      }
    })()
  }

  const handleExportSchedule = (): void => {
    void downloadSchedule(caseId, requestId)
  }

  return (
    <div className="space-y-4 border-t border-slate-100 p-4 dark:border-slate-800">
      <SummaryHeader summary={summary} />
      <ActionButtons
        packageStatus={packageStatus}
        onEdit={onEdit}
        onShowPackageBuild={onShowPackageBuild}
        onExportSchedule={handleExportSchedule}
        onShowScheduleImport={onShowScheduleImport}
        onShowUpload={onShowUpload}
      />
      <StatusOverrideControl
        caseId={caseId}
        requestId={requestId}
        packageStatus={packageStatus}
        returnStatus={returnStatus}
        onOverridden={onRefetch}
      />
      <StallControl
        caseId={caseId}
        requestId={requestId}
        packageStatus={packageStatus}
        stallReason={stallReason}
        onChanged={onRefetch}
      />
      <CollectionTargetsTable
        targets={targets}
        loading={targetsLoading}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        isLocked={isLocked}
        onRemoveSelected={handleRemoveSelected}
        removingTargets={removingTargets}
      />
      <BulkStatusUpdateBar
        selectedCount={selectedIds.size}
        onUpdate={handleBulkUpdate}
        updating={updating}
      />
    </div>
  )
}
